import { Checkbox, InputBase, TableCell, TextField, Tooltip } from '@mui/material';
import { Autocomplete } from '@mui/material';
import { BatchDiagnostic, CarrierUnitDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { CSSProperties, memo, ReactElement, ReactNode, useMemo } from 'react';
import { useTooltipStyles } from '../../../../../../ui/components/pure/list-table/list-table.styles';
import { Colors } from '../../../../../../ui/theme/colors';
import { COURIER_BATCH_FIELD_LABELS, CourierBatchField, CourierBatchRow } from '../../../../logic/courier-batch.map';
import {
  diagnosticsForCarrierCell,
  diagnosticsForCell,
  hasRejectedCarrier,
  isRowLocked,
  isRowSelectable,
  resolveCarrierPickerValue,
  worstSeverity
} from '../../../../logic/courier-batch.utils';
import { BatchImportCouriersActionHandlers } from '../batch-import-couriers-action.hook';
import { CourierBatchRowStatus } from './courier-batch-row-status.component';

/** The text fields the grid edits, in column order. The carrier is picked, not typed. */
export const COURIER_BATCH_TEXT_FIELDS = [
  'firstName',
  'lastName',
  'preferredName',
  'mobile',
  'externalID',
  'username',
  'accessCode'
] as const;

// Per field, so a mobile brings up a phone keypad and an access code a numeric one. The code stays
// text: a leading zero is part of it.
const inputAttributes: Partial<Record<CourierBatchField, { type?: string; inputMode?: 'numeric' | 'tel' }>> = {
  mobile: { type: 'tel', inputMode: 'tel' },
  accessCode: { inputMode: 'numeric' }
};

// The couriers list's row height, so the two tables read alike.
const ROW_HEIGHT = '64px';

/** The `ListTable` classes a cell is styled with, worked out once by the grid and shared by every row. */
export interface CourierBatchCellClasses {
  tableCell: string;
  tableFont: string;
  tableCellContent: string;
}

const severityColours = {
  error: Colors.cancelledDeletedRed,
  warning: Colors.overdueOrange
};

export type CourierBatchRowHandlers = Pick<
  BatchImportCouriersActionHandlers,
  'onCellChange' | 'onCarrierChange' | 'onToggleRow'
>;

interface CourierBatchGridRowProps {
  row: CourierBatchRow;
  /** One-based, as the operator reads it. */
  rowNumber: number;
  units: CarrierUnitDto[];
  handlers: CourierBatchRowHandlers;
  cellClasses: CourierBatchCellClasses;
  /** A validate or upload is running: nothing in the table can be changed until it finishes. */
  isDisabled: boolean;
  /** This row is in a validate request whose response hasn't come back yet. */
  isValidating: boolean;
}

/**
 * The cells of one courier in the grid — the table row itself belongs to the virtualised table.
 * Memoised: the rows array keeps an untouched row's identity, and the handlers are stable, so an
 * edit re-renders only the row it's in.
 */
export const CourierBatchGridRow = memo(function CourierBatchGridRow({
  row,
  rowNumber,
  units,
  handlers,
  cellClasses,
  isDisabled,
  isValidating
}: CourierBatchGridRowProps) {
  const isLocked = isRowLocked(row);
  const isReadOnly = isLocked || isDisabled;

  return (
    <>
      <GridCell classes={cellClasses} style={{ whiteSpace: 'nowrap' }}>
        {/* Disabled for a row with errors, which isn't uploaded, for one in flight or imported, and while a request runs. */}
        <Checkbox
          size="small"
          color="primary"
          checked={row.isSelected}
          disabled={isDisabled || !isRowSelectable(row)}
          onChange={() => handlers.onToggleRow(row.rowID)}
          slotProps={{
            input: { 'aria-label': `Select row ${rowNumber} for upload` }
          }}
        />
        <span style={{ color: Colors.midGray, paddingLeft: '4px' }}>{rowNumber}</span>
      </GridCell>

      {COURIER_BATCH_TEXT_FIELDS.map((field) => (
        <GridCell key={field} classes={cellClasses}>
          <DiagnosticTooltip diagnostics={diagnosticsForCell(row, field)}>
            {(borderColour, isInvalid) => (
              <InputBase
                value={row.values[field] ?? ''}
                onChange={(event) => handlers.onCellChange(row.rowID, field, event.target.value)}
                readOnly={isReadOnly}
                fullWidth
                type={inputAttributes[field]?.type ?? 'text'}
                style={{
                  border: `1px solid ${borderColour ?? Colors.lightGray}`,
                  borderRadius: '4px',
                  padding: '2px 8px',
                  fontSize: 'inherit',
                  backgroundColor: isLocked ? Colors.extraLightGray : Colors.white
                }}
                slotProps={{
                  input: {
                    'aria-label': `${COURIER_BATCH_FIELD_LABELS[field]}, row ${rowNumber}`,
                    'aria-invalid': isInvalid,
                    inputMode: inputAttributes[field]?.inputMode,
                    spellCheck: false
                  }
                }}
              />
            )}
          </DiagnosticTooltip>
        </GridCell>
      ))}

      <GridCell classes={cellClasses}>
        <CarrierPicker row={row} rowNumber={rowNumber} units={units} isLocked={isReadOnly} handlers={handlers} />
      </GridCell>

      <GridCell classes={cellClasses}>
        {/* Uploaded rows aren't sent to a validate, so they keep their status. */}
        <CourierBatchRowStatus row={row} isValidating={isValidating && !isLocked} />
      </GridCell>
    </>
  );
});

/** A body cell as `ListTable` draws one: no padding of its own, the table's font, and a padded inner block. */
function GridCell({
  classes,
  style,
  children
}: {
  classes: CourierBatchCellClasses;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <TableCell className={`${classes.tableCell} ${classes.tableFont}`} style={{ height: ROW_HEIGHT, ...style }}>
      <div className={classes.tableCellContent}>{children}</div>
    </TableCell>
  );
}

function CarrierPicker({
  row,
  rowNumber,
  units,
  isLocked,
  handlers
}: {
  row: CourierBatchRow;
  rowNumber: number;
  units: CarrierUnitDto[];
  isLocked: boolean;
  handlers: CourierBatchRowHandlers;
}) {
  const isCarrierRejected = hasRejectedCarrier(row);
  const match = useMemo(
    () => resolveCarrierPickerValue(row.values, units, row.resolvedCarrier),
    [row.values, units, row.resolvedCarrier]
  );
  // A carrier shown but not among the units — an existing courier's own carrier elsewhere, say — is
  // listed only so the picker can display it, never to choose.
  const displayOnly = useMemo(
    () => (match.unit && !units.includes(match.unit) ? match.unit : undefined),
    [match.unit, units]
  );
  const options = useMemo(() => (displayOnly ? [displayOnly, ...units] : units), [displayOnly, units]);

  return (
    <DiagnosticTooltip diagnostics={diagnosticsForCarrierCell(row)}>
      {(borderColour, isInvalid) => (
        <Autocomplete<CarrierUnitDto, false, boolean, false>
          options={options}
          value={match.unit ?? null}
          onChange={(_event, unit) => handlers.onCarrierChange(row.rowID, unit && unit.id ? unit : undefined)}
          // Clearing a row that shows the default would change nothing.
          disableClearable={match.isDefault}
          getOptionLabel={(unit) => unit.name}
          isOptionEqualToValue={(option, value) => option === value || (!!option.id && option.id === value.id)}
          getOptionDisabled={(unit) => unit === displayOnly}
          disabled={isLocked}
          openOnFocus
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={isCarrierRejected ? 'Pick a carrier' : 'Default carrier'}
              slotProps={{
                ...params.slotProps,

                input: {
                  ...params.slotProps.input,
                  disableUnderline: true,
                  style: {
                    border: `1px solid ${borderColour ?? Colors.lightGray}`,
                    borderRadius: '4px',
                    padding: '0 8px',
                    fontSize: 'inherit'
                  }
                },

                htmlInput: {
                  ...params.slotProps.htmlInput,
                  'aria-label': `${COURIER_BATCH_FIELD_LABELS.groupName}, row ${rowNumber}`,
                  'aria-invalid': isInvalid
                }
              }}
            />
          )}
        />
      )}
    </DiagnosticTooltip>
  );
}

function DiagnosticTooltip({
  diagnostics,
  children
}: {
  diagnostics: BatchDiagnostic[];
  children(borderColour: string | undefined, isInvalid: boolean): ReactElement;
}) {
  // The list table's tooltip look: larger text than Material UI's default, so the messages are readable.
  const tooltipClasses = useTooltipStyles();
  const severity = worstSeverity(diagnostics);
  const borderColour = severity ? severityColours[severity] : undefined;

  // Always wrapped, so the control keeps its place in the tree — and its focus — as diagnostics come
  // and go. An empty title shows no tooltip.
  return (
    <Tooltip arrow classes={tooltipClasses} title={diagnostics.map((diagnostic) => diagnostic.message).join(' ')}>
      <div>{children(borderColour, severity === 'error')}</div>
    </Tooltip>
  );
}
